import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { CharterDataRoomPendingDto } from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { SharedModule } from '@shared/shared.module';

/** Quantos nomes a lista curta mostra antes do "e mais N". */
const VISIBLE_DOCUMENTS = 5;

/**
 * Documentos da pasta de contrato social do Data Room que o Cog ainda não leu, com o botão "Processar documentos do
 * Data Room" (um clique, sem confirmação e sem custo de IA, regra das ações de IA). Duas formas:
 * - `empty`: ocupa o estado vazio da tela quando a empresa não tem vigente (aviso, lista curta e o botão primário);
 * - `compact`: faixa discreta abaixo do cabeçalho quando já existe vigente (botão pequeno, lista sob demanda).
 * Cada nome abre o documento na pasta do Data Room, com o painel de detalhes (`?doc=&panel=details`), pela página.
 */
@Component({
  selector: 'app-charter-data-room-pending',
  imports: [SharedModule, NgTemplateOutlet],
  templateUrl: './charter-data-room-pending.component.html',
  styleUrl: './charter-data-room-pending.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterDataRoomPendingComponent {
  readonly pending = input.required<CharterDataRoomPendingDto>();
  readonly variant = input<'empty' | 'compact'>('empty');
  /** Enviar ao Cog exige `DATAROOM_UPDATE`. */
  readonly canEdit = input(false);
  /** Chamada em andamento (botão em "Enviando..."). */
  readonly processing = input(false);
  /** Já enviado nesta tela: a faixa compacta troca o botão pelo aviso de que a tela se atualiza sozinha. */
  readonly sent = input(false);
  readonly openingDocumentId = input<string | null>(null);

  readonly process = output<void>();
  readonly openDocument = output<string>();
  readonly openDataRoom = output<void>();

  /** Lista da faixa compacta, aberta por "Ver quais". */
  protected readonly expanded = signal(false);

  protected readonly headline = computed(() => {
    const total = this.pending().total;
    if (this.variant() === 'compact') {
      return total === 1
        ? '1 documento novo no Data Room ainda não lido'
        : `${total} documentos novos no Data Room ainda não lidos`;
    }
    return total === 1
      ? '1 documento no Data Room ainda não lido pelo Cog'
      : `${total} documentos no Data Room ainda não lidos pelo Cog`;
  });

  /** "2 ainda sem tipo · 1 classificado e não lido" (só as partes com documento). */
  protected readonly breakdown = computed(() => {
    const { unclassified, typedWithoutAct } = this.pending();
    return [
      unclassified > 0 ? `${unclassified} ainda sem tipo` : null,
      typedWithoutAct > 0
        ? `${typedWithoutAct} ${typedWithoutAct === 1 ? 'classificado' : 'classificados'} e não ${typedWithoutAct === 1 ? 'lido' : 'lidos'}`
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
  });

  protected readonly visibleDocuments = computed(() =>
    this.pending().documents.slice(0, VISIBLE_DOCUMENTS)
  );

  /** Os que ficaram fora da lista curta (inclui os além dos 20 que a API devolve). */
  protected readonly hiddenCount = computed(
    () => this.pending().total - this.visibleDocuments().length
  );

  protected toggle(): void {
    this.expanded.update(value => !value);
  }
}
